import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import Button from "@mui/material/Button";
import useMediaQuery from "@mui/material/useMediaQuery";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";

import { dismissSellerDowngradeNotice } from "../../../api/sellerSubscriptions";
import {
  clearCachedDowngradeNotice,
  useMySellerSubscription,
} from "../../../pages/sellerSubscriptions/useSellerSubscriptionsOffered";
import {
  formatMoney,
  priceChangeTitle,
  priceChangeWarningCopy,
} from "../../../pages/sellerSubscriptions/planFormat";
import SellerPlanChangeModal from "../../../pages/sellerSubscriptions/SellerPlanChangeModal";
import { clearReconnectGuard } from "../../../pages/sellerSubscriptions/reconnectShopify";

/**
 * Site-wide banner for sellers whose current plan has a new price they
 * haven't accepted yet. Hidden for enterprise admins and after accept
 * (GET /me drops pendingPriceChange once billed price matches the plan).
 */
const SellerPriceChangeBanner = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const isMobile = useMediaQuery("(max-width:600px)");
  const { pendingPriceChange, downgradeNotice } = useMySellerSubscription();
  const visible = !!pendingPriceChange;
  const [dismissing, setDismissing] = useState(false);

  useEffect(() => {
    if (!visible) return undefined;
    document.body.classList.add("has-riverr-global-banner");
    return () => document.body.classList.remove("has-riverr-global-banner");
  }, [visible]);

  const dismissNotice = async () => {
    if (dismissing) return;
    setDismissing(true);
    try {
      await dismissSellerDowngradeNotice();
    } catch (_) {
      /* still close locally so they are not stuck */
    }
    clearCachedDowngradeNotice();
    setDismissing(false);
  };

  const upgradeAfterNotice = async () => {
    await dismissNotice();
    // A leftover reconnect guard from earlier testing would block the
    // auto-upgrade and show "Reconnecting didn't fix this store".
    clearReconnectGuard();
    navigate("/subscription?upgrade=1");
  };

  const noticeDialog = (
    <SellerPlanChangeModal
      open={!!downgradeNotice}
      variant="deadline"
      busy={dismissing}
      onPrimary={upgradeAfterNotice}
      onSecondary={dismissNotice}
    />
  );

  if (!visible) return noticeDialog;

  const title = priceChangeTitle(pendingPriceChange);
  const body = priceChangeWarningCopy(pendingPriceChange);
  const acceptLabel = `Accept $${formatMoney(
    pendingPriceChange.newPriceCents
  )}/mo`;
  const onSubscription = location.pathname.startsWith("/subscription");
  const acceptButton = !onSubscription ? (
    <Button
      variant="contained"
      size="small"
      component={Link}
      to="/subscription?accept=1"
      sx={{
        mt: isMobile ? 1 : 0,
        ml: isMobile ? 0 : 1.5,
        width: isMobile ? "100%" : "auto",
        fontWeight: 700,
        textTransform: "none",
        boxShadow: "none",
        bgcolor: "#6b5216",
        "&:hover": { bgcolor: "#564112", boxShadow: "none" },
      }}
    >
      {acceptLabel}
    </Button>
  ) : null;

  if (isMobile) {
    return (
      <>
        <Alert
          severity="warning"
          icon={<WarningAmberRoundedIcon fontSize="inherit" />}
          data-riverr-global-banner
          sx={{
            mt: "60px",
            borderRadius: 0,
            alignItems: "flex-start",
            "& .MuiAlert-message": { width: "100%", p: 0 },
          }}
        >
          <AlertTitle sx={{ fontWeight: 700, mb: 0.5, fontSize: 13 }}>
            {title}
          </AlertTitle>
          <div style={{ fontSize: 12.5, lineHeight: 1.45 }}>{body}</div>
          {acceptButton}
        </Alert>
        {noticeDialog}
      </>
    );
  }

  return (
    <>
      <Alert
        severity="warning"
        icon={<WarningAmberRoundedIcon fontSize="inherit" />}
        data-riverr-global-banner
        sx={{
          mt: "60px",
          borderRadius: 0,
          alignItems: "center",
          "& .MuiAlert-message": {
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            width: "100%",
          },
        }}
      >
        <AlertTitle sx={{ fontWeight: 700, mb: 0, mr: 1 }}>{title}</AlertTitle>
        <span>{body}</span>
        {acceptButton}
      </Alert>
      {noticeDialog}
    </>
  );
};

export default SellerPriceChangeBanner;
